import React, { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer
} from 'recharts';
import { SelectPicker } from 'rsuite';

const ARVLineChart = ({ data, locations, methods, instruments, parameters }) => {
  const [location, setLocation] = useState('');
  const [method, setMethod] = useState('');
  const [instrument, setInstrument] = useState('');
  const [parameter, setParameter] = useState('');

  // Handle cascading filter logic
  const handleLocationChange = (val) => {
    setLocation(val || '');
    setMethod('');
    setInstrument('');
    setParameter('');
  };

  const handleMethodChange = (val) => {
    setMethod(val || '');
    setInstrument('');
    setParameter('');
  };

  const handleInstrumentChange = (val) => {
    setInstrument(val || '');
    setParameter('');
  };

  const handleParameterChange = (val) => {
    setParameter(val || '');
  };

  const filteredData = useMemo(() => {
    if (!data) return [];

    if (!(location && method && instrument && parameter)) return [];

    const result = data.filter((d) => {
      return (
        (!location || d.location === location) &&
        (!method || d.method === method) &&
        (!instrument || d.instrument === instrument) &&
        (!parameter || d.parameter === parameter)
      );
    });

    return [...result].sort((a, b) => a.round.localeCompare(b.round));
  }, [data, location, method, instrument, parameter]);

  const average = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return 0;
    const sum = filteredData.reduce((acc, d) => acc + d.delta, 0);
    return sum / filteredData.length;
  }, [filteredData]);

  return (
    <div className="chartSection">
      {/* Filters */}
      <div className="filterRow">
        <div className="filterComponent">
          <label>Location</label>
          <SelectPicker
            data={locations}
            value={location}
            onChange={handleLocationChange}
            onClean={() => handleLocationChange('')}
            className="select"
          />
        </div>

        <div className="filterComponent">
          <label>Method</label>
          <SelectPicker
            data={methods}
            value={method}
            disabled={!location}
            onChange={handleMethodChange}
            onClean={() => handleMethodChange('')}
            className="select"
          />
        </div>

        <div className="filterComponent">
          <label>Instrument</label>
          <SelectPicker
            data={instruments}
            value={instrument}
            disabled={!method}
            onChange={handleInstrumentChange}
            onClean={() => handleInstrumentChange('')}
            className="select"
          />
        </div>

        <div className="filterComponent">
          <label>Parameter</label>
          <SelectPicker
            data={parameters}
            value={parameter}
            disabled={!instrument}
            onChange={handleParameterChange}
            onClean={() => handleParameterChange('')}
            className="select"
          />
        </div>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={
            filteredData.length > 0
              ? filteredData.map((d) => ({
                  ...d,
                  avg: average,
                  expected: 0
                }))
              : [{ round: '–', delta: null, avg: average, expected: 0 }]
          }
          margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
        >
          <CartesianGrid strokeDasharray="3 3" />

          <XAxis dataKey="round" />

          <YAxis domain={['auto', 'auto']} allowDataOverflow padding={{ bottom: 50, top: 50 }} />

          <Tooltip
            formatter={(value, name) => {
              if (typeof value === 'number') {
                return [value.toFixed(3), name];
              }
              return [value, name];
            }}
          />

          <Legend align="center" verticalAlign="top" wrapperStyle={{ paddingBottom: 10 }} />

          <ReferenceLine y={0} stroke="#000" strokeDasharray="2 2" />

          {filteredData.length > 0 && (
            <Line
              type="linear"
              dataKey="delta"
              stroke="#1f77b4"
              strokeWidth={2}
              name="Delta"
              dot={{ r: 3 }}
            />
          )}
          {/* Always render Expected */}
          <Line
            type="linear"
            dataKey="expected"
            stroke="#d62728"
            strokeDasharray="12 6"
            strokeWidth={2}
            name="Expected Value"
            dot={false}
            isAnimationActive={false}
          />
          {/* Always render Average */}
          <Line
            type="linear"
            dataKey="avg"
            stroke="#2ca02c"
            strokeDasharray="5 5"
            strokeWidth={2}
            name={`Average (${average.toFixed(2)})`}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ARVLineChart;
